import { useCallback, useEffect, useState } from 'react';

import { type StagedFile } from 'modules/common/components/FileUploadArea';
import { Document } from 'modules/common/interfaces/documents';
import useCompleteMemberTask from 'modules/Tasks/hooks/useCompleteMemberTask';
import { SubmitActionTask } from 'modules/Tasks/types';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

interface UseTaskDocumentUploadParams {
    task: SubmitActionTask;
    documentTypeId: string | undefined;
    open: boolean;
    onClose: () => void;
}

/**
 * Owns the deferred-upload state machine for the task upload modal: file staging,
 * the Save-to-commit trigger, completion via the MemberOperator SDK, and reset on close.
 */
export default function useTaskDocumentUpload({ task, documentTypeId, open, onClose }: UseTaskDocumentUploadParams) {
    const completeMemberTask = useCompleteMemberTask(task, onClose);

    const [hasStagedFile, setHasStagedFile] = useState(false);
    const [uploadTrigger, setUploadTrigger] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    // Reset whenever the modal closes.
    useEffect(() => {
        if (!open) {
            setHasStagedFile(false);
            setUploadTrigger(0);
            setIsUploading(false);
        }
    }, [open]);

    const handleStagedFilesChange = useCallback((files: StagedFile[]) => {
        setHasStagedFile(files.length > 0);
    }, []);

    // Save commits the staged files; FileUploadArea uploads on the trigger bump.
    const handleSave = useCallback(() => {
        if (!hasStagedFile) {
            return;
        }
        setIsUploading(true);
        setUploadTrigger((prev) => prev + 1);
    }, [hasStagedFile]);

    const handleUploadComplete = useCallback(
        (docs: Document[]) => {
            // No docs => the upload failed; keep the modal open so the member can retry.
            if (docs.length === 0) {
                setIsUploading(false);
                return;
            }
            completeMemberTask({
                trackingEvent: 'Document Upload Completed',
                trackingProperties: { document_type_id: documentTypeId },
                completionPayload: {
                    document_uuids: docs.map((d) => d.uuid),
                    document_type_id: documentTypeId,
                },
                onError: () => setIsUploading(false),
            });
        },
        [completeMemberTask, documentTypeId]
    );

    const handleUploadError = useCallback(
        (error: Error) => {
            setIsUploading(false);
            sendDatadogRumError('Task document upload failed', error, {
                taskId: task.id,
                document_type_id: documentTypeId,
            });
        },
        [task.id, documentTypeId]
    );

    return {
        isUploading,
        hasStagedFile,
        uploadTrigger,
        handleSave,
        handleStagedFilesChange,
        handleUploadComplete,
        handleUploadError,
    };
}
